/**
 * Shared chat-prompt builder for net-impact-lens click surfaces (PLT-4700).
 *
 * Five click surfaces on the company net-impact lens route into the same
 * `get_contributing_products` chat MCP card: the dimension label, the
 * dimension bar (per valence), the impact-category title, the
 * impact-category bar in the lens carousel, and the track-section
 * `TimeTrackingImpactChart`'s impact bar. Before this module they each
 * sent a different prompt:
 *
 *   - dimension bar:                  "What is driving the {valence} {dimension} impact of {entity}?"
 *   - dimension label:                "What is driving the {dimension} impact of {entity}?"
 *   - impact title:                   "Explain the {category} impact category — what it covers, what topics are considered, and what's driving {entity}'s net result here."
 *   - impact bar (lens carousel):     "What are the top contributing products for {category} at {entity}?"
 *   - impact bar (track-section):     "What are the top contributing products for {category} at {entity}?"
 *
 * Because the LLM's tool-routing is prompt-shape-sensitive, this meant
 * the surfaces could land on different MCP cards, defeating the
 * unified-card design (one card, one mental model). Per the PLT-4700
 * design discussion in Slack: "Same for everything." This helper emits
 * one prompt shape — `"What is driving the {grain} impact at {entity}?"`
 * — at whichever grain the user clicked. The about-panel inside the
 * contributing-products card carries the explainer (topics considered,
 * examples) the old impact-title prompt asked for in prose; the in-card
 * valence pill carries the positive/negative filter the old
 * dimension-bar prompt encoded into wording. Both pieces of the legacy
 * variants are now in the card UI itself — the prompt no longer has to
 * carry them.
 *
 * The grain word is caller-supplied (dimension surfaces use the
 * adjective form; impact surfaces use the friendly category name) so
 * the prose reads naturally for each surface — but the question shape
 * is identical at both grains so the LLM sees the same shape regardless
 * of where the user clicked from.
 */
import { Dimension } from '@upright/common/model/types';

/**
 * Adjective form of a dimension used in the chat prompt
 * ("environmental", "social", "health-related", "knowledge-related").
 *
 * Lifted out of the dimension-bar / dimension-label files so all four
 * click surfaces share one mapping. Keep wording stable: the LLM's
 * tool-routing is prompt-shape-sensitive and the cached system prompt
 * mirrors a "what is driving the X impact at Y" template.
 */
export function dimensionAdjective(dimension: Dimension): string {
  switch (dimension) {
    case Dimension.SOCIAL:
      return 'social';
    case Dimension.KNOWLEDGE:
      return 'knowledge-related';
    case Dimension.HEALTH:
      return 'health-related';
    case Dimension.ENVIRONMENT:
      return 'environmental';
  }
}

/**
 * Build the unified "what is driving" chat prompt for any of the four
 * net-impact click surfaces. `grainWord` is the dimension adjective
 * (e.g. "environmental") for dimension surfaces, or the friendly
 * category name (e.g. "GHG emissions") for impact-category surfaces.
 */
export function buildDrivingImpactPrompt(grainWord: string, entityName: string): string {
  return `What is driving the ${grainWord} impact at ${entityName}?`;
}
